import { faArrowDown, faCheck, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Alert, Button, ButtonGroup, Checkbox, Col, FlexboxGrid, Message, Modal } from 'rsuite';
import { authHeader } from '../../../redux/helpers';
import { axiosService } from '../../../redux/services';
import TCMSensor from '../../../handlers/ipCanDevices/TCMSensor';

type Props = {
    sensorToCopy: TCMSensor | null;
    sensorsToCopyOn: Array<TCMSensor>;
    show: boolean;
    onHide: Function;
} & WrappedComponentProps;

type State = {
    isLoading: boolean;
    hasError: boolean;
    error: any | null;
    formValue: FormValue;
};

type FormValue = {
    calibrationConfig: boolean;
    maxOccupiedConfig: boolean;
    maxFreeConfig: boolean;
    colorForcing: boolean;
    colorFree: boolean;
    colorOccupied: boolean;
    colorOverstay: boolean;
    colorOverstayFree: boolean;
};

class CopyTcmSensorModal extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isLoading: false,
            hasError: false,
            error: null,
            formValue: {
                calibrationConfig: true,
                maxOccupiedConfig: true,
                maxFreeConfig: true,
                colorForcing: false,
                colorFree: false,
                colorOccupied: false,
                colorOverstay: false,
                colorOverstayFree: false,
            },
        };

        this.launchCopy = this.launchCopy.bind(this);
        this.closeModal = this.closeModal.bind(this);
    }

    launchCopy = () => {
        this.setState({
            isLoading: true,
            hasError: false,
            error: null,
        });

        axiosService
            .getAxios()
            .put(
                '/devices/tcm-sensor/copyConfig',
                {
                    id: this.props.sensorToCopy!.id,
                    tabId: this.props.sensorsToCopyOn.map(sensor => sensor.id),
                    calibrationConfig: this.state.formValue.calibrationConfig,
                    maxOccupiedConfig: this.state.formValue.maxOccupiedConfig,
                    maxFreeConfig: this.state.formValue.maxFreeConfig,
                    colorForcing: this.state.formValue.colorForcing,
                    colorFree: this.state.formValue.colorFree,
                    colorOccupied: this.state.formValue.colorOccupied,
                    colorOverstay: this.state.formValue.colorOverstay,
                    colorOverstayFree: this.state.formValue.colorOverstayFree,
                },
                {
                    headers: authHeader(),
                }
            )
            .then(() => {
                this.setState({
                    isLoading: false,
                });
            })
            .then(() => {
                Alert.success(
                    this.props.intl.formatMessage({
                        id: 'tcmSensors.copy.success',
                    })
                );

                this.closeModal(true);
            })
            .catch(err => {
                this.setState({
                    hasError: err,
                    error: err,
                });
            });
    };

    closeModal = (success: boolean = false) => {
        if (!this.state.isLoading) {
            this.props.onHide(success);
        }
    };

    handleChange = (value, checked) => {
        this.setState({
            formValue: {
                ...this.state.formValue,
                [value]: checked,
            },
        });
    };

    render = () => {
        if (this.props.sensorToCopy) {
            const { calibrationConfig, maxOccupiedConfig, maxFreeConfig } = this.state.formValue;

            const shouldAllowSensorCopy =
                calibrationConfig ||
                maxOccupiedConfig ||
                maxFreeConfig ||
                this.state.formValue.colorForcing ||
                this.state.formValue.colorFree ||
                this.state.formValue.colorOccupied ||
                this.state.formValue.colorOverstay ||
                this.state.formValue.colorOverstayFree;

            return (
                <Modal backdrop="static" show={this.props.show} onHide={() => this.closeModal()}>
                    <Modal.Header>
                        <Modal.Title>
                            <FormattedMessage
                                id="tcmSensors.copy.title"
                                values={{ bus: this.props.sensorToCopy.bus, address: this.props.sensorToCopy.deviceId }}
                            />
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {this.state.hasError && (
                            <Message
                                type="error"
                                description={this.props.intl.formatMessage(
                                    {
                                        id: 'tcmSensors.copy.error',
                                    },
                                    {
                                        error: this.state.error,
                                    }
                                )}
                            />
                        )}
                        <p>
                            <FormattedMessage id="tcmSensors.copy.body" />
                        </p>
                        <FlexboxGrid align="middle" justify="center">
                            <FlexboxGrid.Item componentClass={Col} xs={3}>
                                <div className="sensor-to-copy">
                                    {this.props.sensorToCopy.bus + 1} - {this.props.sensorToCopy.deviceId}
                                </div>
                            </FlexboxGrid.Item>
                        </FlexboxGrid>
                        <FlexboxGrid align="middle" justify="center" className="margin-top-10 margin-bottom-10">
                            <FlexboxGrid.Item>
                                <FontAwesomeIcon icon={faArrowDown} size="2x" style={{ textAlign: 'center' }} />
                            </FlexboxGrid.Item>
                        </FlexboxGrid>
                        <FlexboxGrid align="middle" justify="center">
                            {this.props.sensorsToCopyOn.map((item: TCMSensor) => (
                                <FlexboxGrid.Item componentClass={Col} xs={3} className="margin-bottom-5">
                                    <div className="sensor-to-be-copied">
                                        {item.bus + 1} - {item.deviceId}
                                    </div>
                                </FlexboxGrid.Item>
                            ))}
                        </FlexboxGrid>

                        <FlexboxGrid
                            style={{ backgroundColor: '#CFCFCF' }}
                            align="middle"
                            justify="center"
                            className="margin-top-10">
                            <FlexboxGrid.Item>
                                <Checkbox
                                    value="calibrationConfig"
                                    checked={this.state.formValue.calibrationConfig}
                                    onChange={this.handleChange}>
                                    <FormattedMessage id="tcmSensors.copy.calibrationConfig" />
                                </Checkbox>
                                <Checkbox
                                    value="maxOccupiedConfig"
                                    checked={this.state.formValue.maxOccupiedConfig}
                                    onChange={this.handleChange}>
                                    <FormattedMessage id="tcmSensors.copy.maxOccupiedConfig" />
                                </Checkbox>
                                <Checkbox
                                    value="maxFreeConfig"
                                    checked={this.state.formValue.maxFreeConfig}
                                    onChange={this.handleChange}>
                                    <FormattedMessage id="tcmSensors.copy.maxFreeConfig" />
                                </Checkbox>
                                <Checkbox
                                    value="colorForcing"
                                    checked={this.state.formValue.colorForcing}
                                    onChange={this.handleChange}>
                                    <FormattedMessage id="tcmSensors.copy.colorForcing" />
                                </Checkbox>
                                <Checkbox
                                    value="colorFree"
                                    checked={this.state.formValue.colorFree}
                                    onChange={this.handleChange}>
                                    <FormattedMessage id="tcmSensors.copy.colorFree" />
                                </Checkbox>
                                <Checkbox
                                    value="colorOccupied"
                                    checked={this.state.formValue.colorOccupied}
                                    onChange={this.handleChange}>
                                    <FormattedMessage id="tcmSensors.copy.colorOccupied" />
                                </Checkbox>
                                <Checkbox
                                    value="colorOverstay"
                                    checked={this.state.formValue.colorOverstay}
                                    onChange={this.handleChange}>
                                    <FormattedMessage id="tcmSensors.copy.colorOverstay" />
                                </Checkbox>
                                <Checkbox
                                    value="colorOverstayFree"
                                    checked={this.state.formValue.colorOverstayFree}
                                    onChange={this.handleChange}>
                                    <FormattedMessage id="tcmSensors.copy.colorOverstayFree" />
                                </Checkbox>
                            </FlexboxGrid.Item>
                        </FlexboxGrid>
                    </Modal.Body>
                    <Modal.Footer>
                        <ButtonGroup>
                            <Button
                                data-cy="ipCanDevices-lpSensor-copy-cancel"
                                onClick={() => this.closeModal()}
                                color="red"
                                disabled={this.state.isLoading}>
                                <FontAwesomeIcon icon={faTimesCircle} />
                            </Button>
                            <Button
                                data-cy="ipCanDevices-lpSensor-copy-valid"
                                onClick={() => this.launchCopy()}
                                appearance="primary"
                                loading={this.state.isLoading}
                                disabled={!shouldAllowSensorCopy}>
                                <FontAwesomeIcon icon={faCheck} />
                            </Button>
                        </ButtonGroup>
                    </Modal.Footer>
                </Modal>
            );
        }

        return null;
    };
}

export default injectIntl(CopyTcmSensorModal);
